import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Modal, Select, Spinner } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { useAuthStore } from '../../context/auth'
import type { AnamnesisField, AnamnesisFieldType, AnamnesisSection, AnamnesisTemplate } from '../../types/api'

/**
 * Mantenedor de plantillas de anamnesis (H2). Cada profesional administra las
 * suyas: son parte de su criterio clínico, no configuración de la clínica.
 */

const FIELD_TYPE_LABELS: Record<AnamnesisFieldType, string> = {
  TEXT: 'Texto corto',
  TEXTAREA: 'Texto largo',
  NUMBER: 'Número',
  SELECT: 'Lista de opciones',
  BOOLEAN: 'Sí / No',
}

/** Clave estable a partir del título del campo (el usuario no la escribe). */
function slugKey(label: string, taken: Set<string>): string {
  const base =
    label
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'campo'
  let key = base
  let n = 2
  while (taken.has(key)) key = `${base}_${n++}`
  return key
}

const EMPTY_SECTION: AnamnesisSection = { title: 'Nueva sección', fields: [] }

function TemplateEditor({
  initial,
  onClose,
}: {
  initial?: AnamnesisTemplate
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [sections, setSections] = useState<AnamnesisSection[]>(initial?.sections ?? [{ ...EMPTY_SECTION, title: 'Anamnesis' }])
  const [error, setError] = useState('')

  const usedKeys = new Set(sections.flatMap((s) => s.fields.map((f) => f.key)))

  function updateSection(index: number, patch: Partial<AnamnesisSection>) {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  }

  function addField(sectionIndex: number) {
    const field: AnamnesisField = { key: slugKey('campo', usedKeys), label: '', type: 'TEXT', required: false }
    updateSection(sectionIndex, { fields: [...sections[sectionIndex].fields, field] })
  }

  function updateField(sectionIndex: number, fieldIndex: number, patch: Partial<AnamnesisField>) {
    const fields = sections[sectionIndex].fields.map((f, i) => {
      if (i !== fieldIndex) return f
      const next = { ...f, ...patch }
      // La clave se deriva del título mientras el campo sea nuevo, para que el
      // usuario no tenga que inventarse identificadores.
      if (patch.label !== undefined && !initial) {
        const taken = new Set([...usedKeys].filter((k) => k !== f.key))
        next.key = slugKey(patch.label, taken)
      }
      return next
    })
    updateSection(sectionIndex, { fields })
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        description: description || null,
        // El backend rechaza secciones vacías; las filtramos antes de enviar.
        sections: sections
          .filter((s) => s.fields.length > 0)
          .map((s) => ({
            title: s.title,
            fields: s.fields.map((f) => ({
              key: f.key,
              label: f.label,
              type: f.type,
              required: Boolean(f.required),
              ...(f.type === 'SELECT' ? { options: f.options ?? [] } : {}),
            })),
          })),
      }
      return initial
        ? (await api.patch(`/anamnesis-templates/${initial.id}`, payload)).data
        : (await api.post('/anamnesis-templates', payload)).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anamnesis-templates'] })
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Modal title={initial ? 'Editar plantilla' : 'Nueva plantilla de anamnesis'} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="p. ej. Consulta traumatológica deportiva" />
          </Field>
          <Field label="Descripción (opcional)">
            <Input value={description ?? ''} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>

        {sections.map((section, si) => (
          <div key={si} className="space-y-3 rounded-lg border border-steel-100 p-3">
            <div className="flex items-center gap-2">
              <Input value={section.title} onChange={(e) => updateSection(si, { title: e.target.value })} className="font-semibold" />
              <Button variant="ghost" onClick={() => setSections(sections.filter((_, i) => i !== si))} aria-label="Quitar sección">
                <Icon name="close" className="h-4 w-4" />
              </Button>
            </div>

            {section.fields.length === 0 && <p className="text-xs text-steel-500">Sin campos todavía.</p>}
            {section.fields.map((field, fi) => (
              <div key={fi} className="grid items-end gap-2 rounded-lg bg-surface p-2 sm:grid-cols-[2fr_1fr_auto_auto]">
                <Field label="Pregunta">
                  <Input value={field.label} onChange={(e) => updateField(si, fi, { label: e.target.value })} />
                </Field>
                <Field label="Tipo">
                  <Select value={field.type} onChange={(e) => updateField(si, fi, { type: e.target.value as AnamnesisFieldType })}>
                    {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </Field>
                <label className="flex items-center gap-1.5 pb-2 text-xs text-steel-700">
                  <input
                    type="checkbox"
                    checked={Boolean(field.required)}
                    onChange={(e) => updateField(si, fi, { required: e.target.checked })}
                    className="h-4 w-4 accent-brand-600"
                  />
                  Obligatorio
                </label>
                <Button
                  variant="ghost"
                  className="mb-1"
                  aria-label="Quitar campo"
                  onClick={() => updateSection(si, { fields: section.fields.filter((_, i) => i !== fi) })}
                >
                  <Icon name="close" className="h-4 w-4" />
                </Button>
                {field.type === 'SELECT' && (
                  <div className="sm:col-span-4">
                    <Field label="Opciones (separadas por coma)">
                      <Input
                        value={(field.options ?? []).join(', ')}
                        onChange={(e) =>
                          updateField(si, fi, {
                            options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean),
                          })
                        }
                      />
                    </Field>
                  </div>
                )}
              </div>
            ))}
            <Button variant="secondary" onClick={() => addField(si)}>+ Agregar pregunta</Button>
          </div>
        ))}

        <Button variant="secondary" onClick={() => setSections([...sections, { ...EMPTY_SECTION }])}>
          + Agregar sección
        </Button>

        <ErrorText>{error}</ErrorText>
        <div className="flex gap-2">
          <Button variant="cta" disabled={save.isPending || !name} onClick={() => save.mutate()}>
            {save.isPending ? 'Guardando…' : 'Guardar plantilla'}
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        </div>
      </div>
    </Modal>
  )
}

export function AnamnesisTemplatesPage() {
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<AnamnesisTemplate | null>(null)
  const [creating, setCreating] = useState(false)

  const templates = useQuery({
    queryKey: ['anamnesis-templates'],
    queryFn: async () => (await api.get<AnamnesisTemplate[]>('/anamnesis-templates')).data,
  })

  const deactivate = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/anamnesis-templates/${id}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['anamnesis-templates'] }),
  })

  const isAdmin = user?.role === 'ADMIN'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">
            {isAdmin ? 'Plantillas de anamnesis' : 'Mis plantillas de anamnesis'}
          </h1>
          <p className="text-sm text-steel-600">
            Cuestionarios que se precargan al atender. Cada profesional mantiene los suyos.
          </p>
        </div>
        {!isAdmin && (
          <Button variant="cta" onClick={() => setCreating(true)}>+ Nueva plantilla</Button>
        )}
      </div>

      {templates.isLoading ? (
        <Spinner />
      ) : (templates.data ?? []).length === 0 ? (
        <EmptyState message="Sin plantillas todavía. Crea la primera y quedará disponible al atender." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {(templates.data ?? []).map((t) => (
            <Card
              key={t.id}
              title={t.name}
              actions={
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={() => setEditing(t)}>Editar</Button>
                  <Button variant="ghost" onClick={() => deactivate.mutate(t.id)}>Desactivar</Button>
                </div>
              }
            >
              {t.description && <p className="mb-2 text-sm text-steel-600">{t.description}</p>}
              {isAdmin && t.professional && (
                <Badge className="mb-2 bg-steel-100 text-steel-700">
                  {t.professional.firstName} {t.professional.lastName}
                </Badge>
              )}
              <ul className="space-y-1.5 text-sm">
                {t.sections.map((s) => (
                  <li key={s.title}>
                    <span className="font-medium text-ink">{s.title}</span>
                    <span className="text-steel-500"> · {s.fields.length} {s.fields.length === 1 ? 'pregunta' : 'preguntas'}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {creating && <TemplateEditor onClose={() => setCreating(false)} />}
      {editing && <TemplateEditor initial={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}
